import { viewedElementTrackerQueryOptions } from '@/features/shared/services/ViewedElementTracker/queries'
import { type ViewedElementTracker } from '@/features/shared/services/ViewedElementTracker/types'
import { apiPost, apiPut } from '@/features/shared/utils/dataFetching'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export const useMutationIncrementViewedElementTracker = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (viewedElementTrackerId: number) =>
      apiPost<ViewedElementTracker>({
        config: {
          url: `view-tracker/v1/views/${viewedElementTrackerId}/increment`,
        },
        fetcherName: 'incrementViewedElementTracker',
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: viewedElementTrackerQueryOptions.queryKey,
      }),
  })
}

/**
 * V2 was introduced to support CMS dismissibility, where this endpoint is more flexible.
 * This ticket captures this work: https://app.clickup.com/t/86b6ken5a
 */
export const useMutationIncrementViewedElementTrackerV2 = (
  {
    refetch,
  }: {
    refetch: boolean
  } = { refetch: true },
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (viewedElementTrackerId: string) =>
      apiPost<ViewedElementTracker>({
        config: {
          url: `view-tracker/v2/views/${viewedElementTrackerId}/increment`,
        },
        fetcherName: 'incrementViewedElementTracker',
      }),
    onSuccess: () =>
      // cms dismissals should not result in refetching
      refetch
        ? queryClient.invalidateQueries({
            queryKey: viewedElementTrackerQueryOptions.queryKey,
          })
        : Promise.resolve(),
  })
}

export const useMutationOptOutViewedElementTracker = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (viewedElementTrackerId: number) =>
      apiPut<ViewedElementTracker>({
        config: {
          url: `view-tracker/v1/views/${viewedElementTrackerId}/optOut`,
        },
        fetcherName: 'optOutViewedElementTracker',
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: viewedElementTrackerQueryOptions.queryKey,
      }),
  })
}
